import * as React from 'react';
import * as styled from './styled';
import {IngredientObject} from 'api/ingredient/types';
import {getIngredients} from 'api/ingredient/requests';
import {InstanceSelect, InstanceSelectProps} from 'components/InstanceSelect';
import Dropdown from 'components/Dropdown';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {SelectIngredientProps} from './types';

/**
 *
 * @param {SelectIngredientProps} props
 * @return {React.ReactElement<SelectIngredientProps>}
 * @constructor
 */
export default function SelectIngredient(
    props: SelectIngredientProps,
): React.ReactElement<SelectIngredientProps> {
  const [
    inputValue, setInputValue,
  ] = React.useState<React.HTMLAttributes<HTMLInputElement>['defaultValue']>();

  const [
    ingredients, setIngredients,
  ] = React.useState<IngredientObject[]>([]);

  const [
    hasMore, loadMore,
  ] = React.useState<boolean>(true);

  React.useEffect(() => {
    fetchIngredients(0, true);
  }, [inputValue]);

  /**
   * Fetch the ingredients.
   * @param {number} page
   * @param {boolean} replace
   * @param {number} limit
   */
  function fetchIngredients(page: number, replace = false, limit = 5): void {
    if (hasMore) {
      getIngredients({
        title: inputValue ? String(inputValue) : undefined,
        limit: String(limit),
        skip: String(page * limit),
      }).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          if (replace) {
            setIngredients(response);
          } else {
            setIngredients([
              ...ingredients,
              ...response,
            ]);
          }

          if (response.length === 0) {
            loadMore(false);
          }
        }
      });
    }
  }

  /**
   * The input props.
   * @type {InstanceSelectProps['input']}
   */
  const input: InstanceSelectProps['input'] = {
    ...props.input,
    placeholder: 'Выберите ингредиент из каталога или создайте новый',
    onChange: handleInputChange,
  };

  /**
   * Render ingredients.
   * @return {JSX.Element}
   */
  function renderIngredients(): JSX.Element {
    /**
     * Render the ingredient.
     * @param {IngredientObject} ingredient
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderIngredient(
        ingredient: IngredientObject,
        index: number,
    ): JSX.Element {
      /**
       * The click event handler.
       * @return {void}
       */
      function handleClick(): void {
        props.onSelect && (
          props.onSelect(ingredient)
        );
      }

      return (
        <styled.Ingredient type="button" onClick={handleClick}>
          <Dropdown.Item>
            {ingredient.title}
          </Dropdown.Item>
        </styled.Ingredient>
      );
    }
    return (
      <React.Fragment>
        {ingredients.map((ingredient, index) => (
          renderIngredient(ingredient, index)
        ))}
      </React.Fragment>
    );
  }

  /**
   * The on input change event handler.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  function handleInputChange(
      event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    setInputValue(event.target.value);
  }

  /**
   * The on add click event handler.
   * @param {React.HTMLAttributes<HTMLInputElement>["defaultValue"]} value
   */
  function handleAddClick(
      value: React.HTMLAttributes<HTMLInputElement>['defaultValue'],
  ): void {
    console.log('create a ', value);
  }

  return (
    <InstanceSelect {...props} input={input} onAddClick={handleAddClick}>
      {renderIngredients()}
    </InstanceSelect>
  );
}
